import React, { memo, useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, type ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { theme } from '@/theme/theme';

type PaginationItem = number | 'ellipsis';

export type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  showFirstLast?: boolean;
  showPrevNext?: boolean;
  firstLabel?: string;
  lastLabel?: string;
  style?: ViewStyle | ViewStyle[];
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const getPaginationItems = (currentPage: number, totalPages: number): PaginationItem[] => {
  if (totalPages <= 1) return [1];

  const groupSize = 3;

  if (totalPages <= groupSize) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  let start = 1;

  if (currentPage <= 2) {
    start = 1;
  } else if (currentPage >= totalPages - 1) {
    start = totalPages - groupSize + 1;
  } else {
    start = currentPage - 1;
  }

  const end = Math.min(start + groupSize - 1, totalPages);

  const pages: PaginationItem[] = [];
  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (end < totalPages - 1) {
    pages.push('ellipsis', totalPages);
  } else if (end === totalPages - 1) {
    pages.push(totalPages);
  }

  return pages;
};

type ControlButtonProps = {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  kind?: 'text' | 'icon' | 'page';
  active?: boolean;
  iconDirection?: 'left' | 'right';
};

const ControlButton = memo(
  ({
    label,
    onPress,
    disabled,
    kind = 'page',
    active = false,
    iconDirection,
  }: ControlButtonProps) => {
    const styles = useStyles();
    const { scale } = useScaling();
    const isIcon = kind === 'icon';
    const isText = kind === 'text';
    const textColor = active ? colors['0'] : disabled ? colors['5'] : colors['10'];

    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        style={({ pressed }) => [
          styles.baseControl,
          isText && styles.textControl,
          isIcon && styles.iconControl,
          kind === 'page' && styles.pageControl,
          active && styles.activePageControl,
          pressed && !disabled && styles.pressed,
          disabled && styles.disabled,
        ]}>
        {isIcon ? (
          <SvgIcon
            name="IcPaginationArrow"
            width={scale(10)}
            height={scale(10)}
            color={textColor}
            style={iconDirection === 'left' ? styles.arrowLeft : undefined}
          />
        ) : (
          <Text
            fontFamily="Inter"
            fontSize={scale(10)}
            fontWeight="600"
            lineHeight={scale(10)}
            style={[styles.label, { color: textColor }]}>
            {label}
          </Text>
        )}
      </Pressable>
    );
  },
);

ControlButton.displayName = 'ControlButton';

export const Pagination = memo(
  ({
    currentPage,
    totalPages,
    onPageChange,
    showFirstLast = true,
    showPrevNext = true,
    firstLabel = 'First',
    lastLabel = 'Last',
    style,
  }: PaginationProps) => {
    const { scale } = useScaling();
    const safeCurrentPage = clamp(currentPage, 1, Math.max(totalPages, 1));
    const canGoPrev = safeCurrentPage > 1;
    const canGoNext = safeCurrentPage < totalPages;

    const items = useMemo(
      () => getPaginationItems(safeCurrentPage, totalPages),
      [safeCurrentPage, totalPages],
    );

    const goToPage = useCallback(
      (page: number) => {
        const next = clamp(page, 1, totalPages);
        if (next !== safeCurrentPage) {
          onPageChange?.(next);
        }
      },
      [onPageChange, safeCurrentPage, totalPages],
    );

    if (totalPages <= 1) return null;

    return (
      <Stack direction="row" align="center" gap={scale(8)} style={style}>
        {showFirstLast ? (
          <ControlButton
            kind="text"
            label={firstLabel}
            onPress={() => goToPage(1)}
            disabled={!canGoPrev}
          />
        ) : null}

        {showPrevNext ? (
          <ControlButton
            kind="icon"
            label="Prev"
            onPress={() => goToPage(safeCurrentPage - 1)}
            disabled={!canGoPrev}
            iconDirection="left"
          />
        ) : null}

        {items.map((item, index) => {
          if (item === 'ellipsis') {
            return (
              <ControlButton
                key={`ellipsis-${index}`}
                kind="text"
                label="..."
                onPress={() => {}}
                disabled
              />
            );
          }

          return (
            <ControlButton
              key={item}
              kind="page"
              label={`${item}`}
              active={item === safeCurrentPage}
              onPress={() => goToPage(item)}
            />
          );
        })}

        {showPrevNext ? (
          <ControlButton
            kind="icon"
            label="Next"
            onPress={() => goToPage(safeCurrentPage + 1)}
            disabled={!canGoNext}
            iconDirection="right"
          />
        ) : null}

        {showFirstLast ? (
          <ControlButton
            kind="text"
            label={lastLabel}
            onPress={() => goToPage(totalPages)}
            disabled={!canGoNext}
          />
        ) : null}
      </Stack>
    );
  },
);

Pagination.displayName = 'Pagination';

const useStyles = createStyles(({ scale }) => {
  const controlSize = scale({
    phone: 24,
    tablet: 32,
  });
  const borderRadius = scale(8);

  return StyleSheet.create({
    baseControl: {
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius,
      backgroundColor: theme.colors['Backgrounds/Primary'],
      height: controlSize,
      minWidth: controlSize,
      paddingHorizontal: scale({
        phone: 8,
        tablet: 12,
      }),
    },
    textControl: {
      height: controlSize,
      paddingHorizontal: scale(6),
    },
    iconControl: {
      height: controlSize,
    },
    pageControl: {
      height: controlSize,
    },
    activePageControl: {
      backgroundColor: colors['Blue 2'],
    },
    pressed: {
      opacity: 0.8,
    },
    disabled: {
      opacity: 0.6,
    },
    label: {
      textAlign: 'center',
    },
    arrowLeft: {
      transform: [{ rotate: '180deg' }],
    },
  });
});
